import { useState } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'

const NAV_LINKS = [
  { label: 'Dashboard', href: '/dashboard' },
  { label: 'Schedule', href: '/schedule' },
  { label: 'Settings', href: '/settings' },
]

export function Navbar() {
  const { pathname } = useLocation()
  const user = useAuthStore((s) => s.user)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const [menuOpen, setMenuOpen] = useState(false)

  const showUpgrade = user?.tier !== 'agency'
  const upgradeLabel = user?.tier === 'pro' ? 'Go Agency ↑' : 'Upgrade to Pro ↑'

  return (
    <>
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-outline-variant/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="5.5" cy="18.5" r="2" fill="#818cf8"/>
              <path d="M5.5 13A5.5 5.5 0 0 1 11 18.5" stroke="#818cf8" strokeWidth="2" strokeLinecap="round"/>
              <path d="M5.5 8A10.5 10.5 0 0 1 16 18.5" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" opacity="0.65"/>
              <path d="M5.5 3A15.5 15.5 0 0 1 21 18.5" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" opacity="0.35"/>
            </svg>
            <span className="text-lg font-bold tracking-tighter text-white">ekorank</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden sm:flex items-center gap-4">
            <span className="text-xs font-mono text-outline uppercase">{user?.tier}</span>
            {NAV_LINKS.map(({ label, href }) =>
              pathname.startsWith(href) ? (
                <span key={href} className="text-sm text-white font-medium">{label}</span>
              ) : (
                <a key={href} href={href} className="text-sm text-on-surface-variant hover:text-white transition-colors">{label}</a>
              )
            )}
            {showUpgrade && (
              <a
                href="/settings#billing"
                className="text-xs font-semibold px-3 py-1.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/30 hover:text-indigo-200 transition-all whitespace-nowrap"
              >
                {upgradeLabel}
              </a>
            )}
            <button onClick={clearAuth} className="text-sm text-outline hover:text-error transition-colors">
              Sign out
            </button>
          </div>

          {/* Mobile: upgrade pill + hamburger */}
          <div className="flex sm:hidden items-center gap-2">
            {showUpgrade && (
              <a
                href="/settings#billing"
                className="text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 whitespace-nowrap"
              >
                {user?.tier === 'pro' ? 'Agency ↑' : 'Upgrade ↑'}
              </a>
            )}
            <button
              onClick={() => setMenuOpen(true)}
              className="p-1.5 text-on-surface-variant hover:text-white transition-colors"
              aria-label="Open menu"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M3 5h14M3 10h14M3 15h14" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Backdrop */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm sm:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-72 z-50 bg-background border-l border-outline-variant/10 flex flex-col transition-transform duration-200 sm:hidden ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header: account info + close */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-outline-variant/10">
          <div>
            <p className="text-sm font-medium text-white truncate max-w-[160px]">{user?.email}</p>
            <p className="text-xs font-mono text-outline uppercase mt-0.5">{user?.tier}</p>
          </div>
          <button
            onClick={() => setMenuOpen(false)}
            className="p-1.5 text-on-surface-variant hover:text-white transition-colors"
            aria-label="Close menu"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M2 2l14 14M16 2L2 16" />
            </svg>
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex flex-col px-3 py-3 gap-0.5">
          {NAV_LINKS.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              onClick={() => setMenuOpen(false)}
              className={`px-3 py-2.5 rounded-lg text-sm transition-colors ${
                pathname.startsWith(href)
                  ? 'bg-indigo-500/15 text-white font-medium'
                  : 'text-on-surface-variant hover:bg-white/5 hover:text-white'
              }`}
            >
              {label}
            </a>
          ))}
        </nav>

        {/* Upgrade CTA */}
        {showUpgrade && (
          <div className="px-3 pt-1 pb-3">
            <a
              href="/settings#billing"
              onClick={() => setMenuOpen(false)}
              className="block w-full text-center text-sm font-semibold px-4 py-2.5 rounded-lg bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/30 transition-all"
            >
              {upgradeLabel}
            </a>
          </div>
        )}

        {/* Sign out */}
        <div className="mt-auto px-3 py-4 border-t border-outline-variant/10">
          <button
            onClick={() => { clearAuth(); setMenuOpen(false) }}
            className="w-full text-left px-3 py-2.5 rounded-lg text-sm text-outline hover:text-error hover:bg-error/5 transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>
    </>
  )
}
